"use client";

import { Toggle as TogglePrimitive } from "@base-ui/react/toggle";
import { ToggleGroup as ToggleGroupPrimitive } from "@base-ui/react/toggle-group";
import { cva } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

export type SegmentControlSize = "xs" | "compact" | "default";
export type SegmentControlWidthMode = "equal" | "intrinsic";

const segmentControlVariants = cva("relative isolate inline-flex items-center bg-muted p-0.5", {
  defaultVariants: {
    size: "default",
    widthMode: "intrinsic",
  },
  variants: {
    size: {
      // Fits beside a card title (16px text), e.g. the dashboard Performance view switch.
      xs: "h-[26px] gap-0.5 rounded-[var(--paper-radius-control)]",
      compact: "h-8 rounded-[var(--paper-radius-control)]",
      default: "h-9 rounded-[var(--paper-radius-composite)]",
    },
    widthMode: {
      equal: "w-full",
      intrinsic: "w-fit",
    },
  },
});

const segmentControlItemVariants = cva(
  "relative z-1 inline-flex shrink-0 items-center justify-center whitespace-nowrap border border-transparent font-medium text-muted-foreground outline-none ring-ring/15 transition-[background-color,border-color,color,box-shadow] duration-[var(--duration-fast)] hover:bg-control-subtle-hover hover:text-foreground active:border-control-border-hover focus-visible:z-10 focus-visible:border-ring focus-visible:bg-transparent focus-visible:text-foreground focus-visible:ring-2 data-disabled:pointer-events-none data-disabled:opacity-40 data-pressed:text-foreground data-pressed:hover:bg-transparent [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    defaultVariants: {
      size: "default",
      widthMode: "intrinsic",
    },
    variants: {
      size: {
        xs: "h-[22px] gap-1 rounded-[6px] px-2.5 text-xs leading-4 [&_svg:not([class*='size-'])]:size-3",
        compact:
          "h-7 gap-1.5 rounded-[6px] px-2 text-[11px] leading-[14px] [&_svg:not([class*='size-'])]:size-3.5",
        default:
          "h-8 gap-2 rounded-[var(--paper-radius-control)] px-3 text-xs leading-4 [&_svg:not([class*='size-'])]:size-4",
      },
      widthMode: {
        equal: "min-w-0 flex-1",
        intrinsic: "min-w-max",
      },
    },
  },
);

const segmentControlIndicatorVariants = cva(
  "pointer-events-none absolute inset-y-0.5 left-0 z-0 w-0 border border-control-border bg-card opacity-0 shadow-xs transition-[transform,width,opacity] duration-[var(--duration-normal)] ease-[cubic-bezier(0.22,1,0.36,1)] will-change-[transform,width] motion-reduce:transition-none",
  {
    defaultVariants: {
      size: "default",
    },
    variants: {
      size: {
        xs: "rounded-[6px]",
        compact: "rounded-[6px]",
        default: "rounded-[var(--paper-radius-control)]",
      },
    },
  },
);

interface SegmentControlContextValue {
  size: SegmentControlSize;
  widthMode: SegmentControlWidthMode;
}

const SegmentControlContext = React.createContext<SegmentControlContextValue>({
  size: "default",
  widthMode: "intrinsic",
});

type SegmentControlValueProps<Value extends string> =
  | {
      defaultValue: Value;
      value?: never;
    }
  | {
      defaultValue?: never;
      value: Value;
    };

export type SegmentControlProps<Value extends string = string> = Omit<
  ToggleGroupPrimitive.Props<Value>,
  "defaultValue" | "multiple" | "onValueChange" | "orientation" | "value"
> &
  SegmentControlValueProps<Value> & {
    onValueChange?: (value: Value, eventDetails: ToggleGroupPrimitive.ChangeEventDetails) => void;
    size?: SegmentControlSize;
    widthMode?: SegmentControlWidthMode;
  };

export function SegmentControl<Value extends string = string>({
  children,
  className,
  defaultValue,
  onValueChange,
  size = "default",
  value,
  widthMode = "intrinsic",
  ...props
}: SegmentControlProps<Value>): React.ReactElement {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const indicatorRef = React.useRef<HTMLSpanElement>(null);
  const animationFrameRef = React.useRef<number | null>(null);
  const indicatorInitializedRef = React.useRef(false);
  const controlledValues = value === undefined ? undefined : [value];
  const uncontrolledValues = defaultValue === undefined ? undefined : [defaultValue];

  const updateIndicator = React.useCallback((animate: boolean) => {
    const root = rootRef.current;
    const indicator = indicatorRef.current;
    const activeItem = root?.querySelector<HTMLElement>(
      '[data-slot="segment-control-item"][data-pressed]',
    );

    if (!root || !indicator) {
      return;
    }

    if (!activeItem) {
      indicator.style.opacity = "0";
      return;
    }

    const writePosition = () => {
      indicator.style.opacity = "1";
      indicator.style.transform = `translateX(${activeItem.offsetLeft}px)`;
      indicator.style.width = `${activeItem.offsetWidth}px`;
    };

    if (!animate) {
      indicator.style.transition = "none";
      writePosition();
      // Flush the initial or resized position before restoring animated changes.
      void indicator.offsetWidth;
      indicator.style.transition = "";
      return;
    }

    writePosition();
  }, []);

  const scheduleIndicatorUpdate = React.useCallback(
    (animate: boolean) => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        updateIndicator(animate && indicatorInitializedRef.current);
        indicatorInitializedRef.current = true;
        animationFrameRef.current = null;
      });
    },
    [updateIndicator],
  );

  React.useLayoutEffect(() => {
    updateIndicator(indicatorInitializedRef.current);
    indicatorInitializedRef.current = true;
  });

  React.useEffect(() => {
    const root = rootRef.current;

    if (!root || typeof ResizeObserver === "undefined") {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      scheduleIndicatorUpdate(false);
    });
    resizeObserver.observe(root);

    return () => {
      resizeObserver.disconnect();
    };
  }, [scheduleIndicatorUpdate]);

  React.useEffect(
    () => () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    },
    [],
  );

  const handleValueChange = React.useCallback(
    (nextValues: Value[], eventDetails: ToggleGroupPrimitive.ChangeEventDetails) => {
      const nextValue = nextValues[0];

      // A Segment Control represents a required single choice, so pressing the current
      // item again must not clear the group like a regular Toggle Group can.
      if (nextValue === undefined) {
        eventDetails.cancel();
        return;
      }

      onValueChange?.(nextValue, eventDetails);

      if (!eventDetails.isCanceled) {
        scheduleIndicatorUpdate(true);
      }
    },
    [onValueChange, scheduleIndicatorUpdate],
  );

  return (
    <ToggleGroupPrimitive
      {...props}
      className={cn(segmentControlVariants({ size, widthMode }), className)}
      data-size={size}
      data-slot="segment-control"
      data-width-mode={widthMode}
      defaultValue={uncontrolledValues}
      multiple={false}
      onValueChange={handleValueChange}
      orientation="horizontal"
      ref={rootRef}
      value={controlledValues}
    >
      <span
        aria-hidden="true"
        className={segmentControlIndicatorVariants({ size })}
        data-slot="segment-control-indicator"
        ref={indicatorRef}
      />
      <SegmentControlContext.Provider value={{ size, widthMode }}>
        {children}
      </SegmentControlContext.Provider>
    </ToggleGroupPrimitive>
  );
}

export type SegmentControlItemProps<Value extends string = string> =
  TogglePrimitive.Props<Value> & {
    value: Value;
  };

export function SegmentControlItem<Value extends string = string>({
  children,
  className,
  type = "button",
  ...props
}: SegmentControlItemProps<Value>): React.ReactElement {
  const { size, widthMode } = React.useContext(SegmentControlContext);

  return (
    <TogglePrimitive
      className={cn(segmentControlItemVariants({ size, widthMode }), className)}
      data-size={size}
      data-slot="segment-control-item"
      type={type}
      {...props}
    >
      {children}
    </TogglePrimitive>
  );
}
